/**
 * Centralized enums and constants for Imbasion
 *
 * This file contains all enum-like values used throughout the game.
 * Using const objects with 'as const' for type safety and iterability.
 */

// =============================================================================
// ALLEGIANCE - Entity faction/team
// =============================================================================

export const Allegiance = {
  Player: 'player',
  Enemy: 'enemy',
  Neutral: 'neutral',
} as const;

export type Allegiance = (typeof Allegiance)[keyof typeof Allegiance];

// =============================================================================
// ENTITY CATEGORY - Base class type for entities
// =============================================================================

export const EntityCategory = {
  Unit: 'unit',
  Structure: 'structure',
  Collectible: 'collectible',
  Boss: 'boss',
} as const;

export type EntityCategory = (typeof EntityCategory)[keyof typeof EntityCategory];

// =============================================================================
// WEIGHT CLASS - Affects abduction speed
// =============================================================================

export const WeightClass = {
  Light: 'light',
  Medium: 'medium',
  Heavy: 'heavy',
} as const;

export type WeightClass = (typeof WeightClass)[keyof typeof WeightClass];

/**
 * Speed multipliers for each weight class during abduction.
 * Higher = faster abduction.
 */
export const WeightSpeeds: Record<WeightClass, number> = {
  [WeightClass.Light]: 1.5,
  [WeightClass.Medium]: 1.0,
  [WeightClass.Heavy]: 0.5,
};

/**
 * Numeric weight values for C3 lift formula: liftSpeed = (3 * tractorPower) / weightValue
 */
export const WeightValues: Record<WeightClass, number> = {
  [WeightClass.Light]: 1,
  [WeightClass.Medium]: 2,
  [WeightClass.Heavy]: 4,
};

// =============================================================================
// LEVEL LAYER CATEGORY - Types of objects in level layers
// =============================================================================

// Re-export from shared types
export { LevelLayerCategory, type LevelLayerCategory as LevelLayerCategoryType } from '../../../shared/types/LevelTypes';

// =============================================================================
// ENTITY TIMING - Timing constants for entity effects
// =============================================================================

export const EntityTiming = {
  /** Duration of damage flash effect in ms */
  DamageFlashDuration: 100,
  /** Delay before entity is destroyed after death in ms */
  DeathDelay: 100,
  /** Duration of abduction cancel tween in ms */
  AbductionCancelDuration: 300,
} as const;

// =============================================================================
// ABDUCTION - Abduction behavior constants
// =============================================================================

export const AbductionConfig = {
  /** Base lift speed when being abducted (pixels per second at speed 1.0) */
  BaseLiftSpeed: 50,
  /** Default bio matter value */
  DefaultBioMatter: 10,
} as const;

// =============================================================================
// LAYER DEPTHS - Rendering order (higher = on top)
// =============================================================================

export const LayerDepths = {
  // Backgrounds
  Background: 0,
  AirBackground: 1,
  MountainBackground: 2,

  // Game layers (containers from level JSON)
  Floor: 10,
  Destructables: 20,
  Abducteables: 30,
  Entities: 30,          // Same as Abducteables
  OverImages: 40,

  // Gameplay elements
  TractorBurnMark: 24,
  TractorBeam: 25,
  TractorLine: 28,
  Projectiles: 80,
  Player: 100,
  Collectibles: 100,
  AbductionParticles: 150,
  AbductionText: 160,

  // Effects (above gameplay, below UI)
  Effects: 500,          // EffectsSystem sprites & particles
  AoeDebug: 600,

  // UI layers
  HUD: 1000,
  WeaponPanelOverlay: 99,
  WeaponPanelContainer: 100,
  Popup: 2000,
  DeathPopup: 2500,

  // Debug (always on top)
  Debug: 9997,
  DebugGraphics: 9998,
  DebugText: 9999,
  DebugOverlay: 10000,
  DebugTooltip: 10001,
} as const;

export type LayerDepth = (typeof LayerDepths)[keyof typeof LayerDepths];
